/**
 * WIP adjustment reasons — single source of truth for the reason list used by
 * write-down, write-up, agreed-value and credit-note flows.
 *
 * In live mode the configured list comes from the backend
 * (GET /api/settings/write-off-reasons) and nothing is merged into it, so
 * reasons removed in Project Settings → Billing disappear everywhere.
 *
 * In demo/tour mode there is no backend, so the demo list below is used.
 *
 * NOTE: Direction-specific filtering (write-up vs write-down) would require a
 * Direction/Kind column on WriteOffReason in the backend — out of scope for now.
 * Both directions draw from this single combined list.
 */

import { fetchWriteOffReasons, getDataMode } from "@/lib/data-service";

export interface WipAdjustmentReason {
  id?: number;
  code: string;
  label: string;
  isDefault?: boolean;
  sortOrder?: number;
}

export const DEMO_WIP_ADJUSTMENT_REASONS: WipAdjustmentReason[] = [
  // Write-down reasons
  { code: "CLIENT_DISCOUNT", label: "Client Discount", isDefault: true },
  { code: "GOODWILL", label: "Goodwill Gesture" },
  { code: "ERROR_CORRECTION", label: "Error / Correction" },
  { code: "BUDGET_OVERRUN", label: "Budget Overrun" },
  { code: "INTERNAL", label: "Internal — Not Billable" },
  { code: "AGREED_VALUE", label: "Agreed Value Adjustment" },
  // Write-up reasons
  { code: "VALUE_PRICING", label: "Value Pricing / Premium" },
  { code: "SCOPE_INCREASE", label: "Scope Increase / Additional Work" },
  { code: "RUSH_FEE", label: "Rush / Expedited Delivery" },
  { code: "SPECIALIST_PREMIUM", label: "Specialist / Senior Resource Premium" },
  { code: "MARKET_RATE_ADJ", label: "Market Rate Adjustment" },
  { code: "FIXED_FEE_UPLIFT", label: "Fixed Fee Uplift" },
];

export function isDemoDataMode(): boolean {
  return getDataMode() !== "live";
}

/**
 * Reasons available for selection. Demo defaults are only used when there is no
 * backend to talk to; live rows are returned as-is.
 */
export async function loadWipAdjustmentReasons(): Promise<WipAdjustmentReason[]> {
  if (isDemoDataMode()) {
    return DEMO_WIP_ADJUSTMENT_REASONS.map((r) => ({ ...r }));
  }

  const apiReasons = await fetchWriteOffReasons();
  return apiReasons
    .filter((r) => r.isActive !== false)
    .map((r) => ({
      id: r.id,
      code: r.code,
      label: r.label,
      isDefault: r.isDefault,
      sortOrder: r.sortOrder,
    }));
}
